import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bandage, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';

const ANNI = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];
const it = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const tabella = (titolo, righe, intestazioni) => righe.length ? `
  <h3 style="margin:16px 0 4px;font-size:13px">${titolo}</h3>
  <table style="width:100%;border-collapse:collapse" border="1" cellpadding="5">
    <thead><tr>${intestazioni.map(h => `<th align="left" style="font-size:11px">${h}</th>`).join('')}</tr></thead>
    <tbody>${righe.map(r => `<tr>${r.map(c => `<td style="font-size:11px">${c}</td>`).join('')}</tr>`).join('')}</tbody>
  </table>` : '';

export default function ReportLesioni() {
  const [open, setOpen] = useState(false);
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [trimestre, setTrimestre] = useState(String(Math.floor(new Date().getMonth() / 3) + 1));
  const [loading, setLoading] = useState(false);

  const genera = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get('/lesioni/report-trimestrale', { params: { anno, trimestre } });
      const t = r.totali, c = r.confronto;
      openPrintWindow(`Report lesioni ${r.trimestre}T ${r.anno}`, `
        <h2>Report trimestrale delle lesioni da pressione</h2>
        <p><b>Periodo:</b> ${r.trimestre}&deg; trimestre ${r.anno} (dal ${it(r.periodo.da)} al ${it(r.periodo.a)}) &nbsp;&nbsp;
        <b>Ospiti in carico:</b> ${r.ospiti_in_carico}</p>

        <table style="width:100%;border-collapse:collapse" border="1" cellpadding="6">
          <tbody>
            <tr><td><b>Prevalenza (ospiti con lesione)</b></td><td align="center">${t.prevalenza_pct}%</td>
                <td><b>Trimestre precedente</b></td><td align="center">${c.prevalenza_precedente}%</td></tr>
            <tr><td><b>Incidenza lesioni insorte in struttura</b></td><td align="center">${t.nuove_in_struttura} (${t.incidenza_struttura_pct}%)</td>
                <td><b>Precedente</b></td><td align="center">${c.nuove_in_struttura_precedenti}</td></tr>
            <tr><td><b>Lesioni monitorate nel periodo</b></td><td align="center">${t.lesioni_nel_periodo}</td>
                <td><b>Nuove insorgenze totali</b></td><td align="center">${t.nuove}</td></tr>
            <tr><td><b>Lesioni guarite nel periodo</b></td><td align="center">${t.guarite}</td>
                <td><b>Ancora aperte a fine periodo</b></td><td align="center">${t.aperte_a_fine_periodo}</td></tr>
            <tr><td><b>Tempo medio di guarigione</b></td>
                <td align="center">${t.giorni_guarigione_medi !== null ? `${t.giorni_guarigione_medi} giorni` : 'n.d.'}</td>
                <td><b>Intervallo (min - max)</b></td>
                <td align="center">${t.giorni_guarigione_min !== null ? `${t.giorni_guarigione_min} - ${t.giorni_guarigione_max} giorni` : 'n.d.'}</td></tr>
          </tbody>
        </table>
        <p style="font-size:10px;color:#64748b">Prevalenza e incidenza sono calcolate sugli ospiti attualmente in carico
        (${r.ospiti_in_carico}). Il tempo di guarigione e la differenza fra data di insorgenza e data di guarigione.</p>

        ${tabella('Distribuzione per stadiazione', r.per_categoria.map(x => [x.label, x.n]), ['Categoria EPUAP/NPIAP', 'N.'])}
        ${tabella('Distribuzione per sede anatomica', r.per_sede.map(x => [x.sede, x.n]), ['Sede', 'N.'])}
        ${tabella('Elenco delle lesioni', r.elenco.map(x => [
          x.ospite_nome, x.stanza, x.sede, x.categoria, x.origine, it(x.data_insorgenza),
          x.stato === 'guarita' ? `guarita il ${it(x.data_guarigione)}` : 'aperta',
        ]), ['Ospite', 'Camera', 'Sede', 'Stadiazione', 'Origine', 'Insorgenza', 'Stato'])}

        <h3 style="margin:16px 0 4px;font-size:13px">Azioni correttive proposte</h3>
        <ol style="font-size:11px;padding-left:18px">${r.azioni_correttive.map(a => `<li style="margin-bottom:3px">${a}</li>`).join('')}</ol>

        <p style="font-size:11px;margin-top:20px">Report generato il ${it(r.generato_il)} da ${r.generato_da}.</p>
        <table style="width:100%;margin-top:30px;font-size:11px"><tr>
          <td>Il Coordinatore<br/><br/>______________________________</td>
          <td>Il Direttore Sanitario<br/><br/>______________________________</td>
        </tr></table>
      `);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nella generazione del report'); }
    setLoading(false);
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="report-lesioni-btn">
        <Bandage className="w-4 h-4" />Report lesioni
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Report trimestrale delle lesioni</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Include prevalenza, incidenza delle lesioni insorte in struttura, tempi medi di guarigione,
              distribuzione per stadiazione e sede, elenco completo, azioni correttive e spazio firme.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Select value={anno} onValueChange={setAnno}>
                <SelectTrigger data-testid="report-lesioni-anno"><SelectValue /></SelectTrigger>
                <SelectContent>{ANNI.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={trimestre} onValueChange={setTrimestre}>
                <SelectTrigger data-testid="report-lesioni-trimestre"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4].map(t => <SelectItem key={t} value={String(t)}>{t}° trimestre</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={genera} disabled={loading} className="bg-brand hover:bg-brand-dark text-white gap-2" data-testid="genera-report-lesioni-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                {loading ? 'Generazione...' : 'Genera e stampa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
